import { ReactElement, cloneElement, forwardRef } from 'react'
import { css, styled } from 'theme'
import { TagProps } from 'config/types'
import { tooltipStyles } from './styleText'

const TagCss = styled('span', {
  // Reset
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'flexShrink': 0,
  'WebkitTapHighlightColor': 'rgba(0,0,0,0)',
  'transitionButton': '$transition$easeOutQuint',
  'border': '1px solid $inputBorder',
  'p': '$space$4 $space$6',
  'backgroundColor': '$inputBg',
  'color': '$inputLabel',
  'cursor': 'auto',
  'whiteSpace': 'pre',

  // Custom
  'fontSize': '$14',
  'lineHeight': '$lineHeight$20',
  'borderRadius': '$radii$sm',
  'fontWeight': '$medium',
  'maxHeight': 24,
  'svg': {
    'cursor': 'pointer',
    '&:hover': {
      path: {
        stroke: '$primaryBase',
      },
    },
  },
  '&:disabled': {
    backgroundColor: '$primaryDisabled',
    color: '$disabledText',
    cursor: 'not-allowed',
    borderColor: '$primaryDisabled',
  },

  'variants': {
    size: {
      sm: {
        p: '$space$2 $space$4',
        borderRadius: '$radii$xs',
        maxHeight: 26,
      },
      md: {
        p: '$space$3 $space$8',
        borderRadius: '$radii$sm',
        maxHeight: 28,
      },
      lg: {
        borderRadius: '$radii$sm',
        p: '$space$8 $space$14',
        maxHeight: 36,
      },
      xl: {
        borderRadius: '$radii$sm',
        p: '$space$8 $space$14',
        fontSize: '$16',
        lineHeight: '$lineHeight$24',
        maxHeight: 36,
      },
      rounded: {
        borderRadius: '$radii$md',
        p: '$space$2 $space$10',
        maxHeight: 28,
      },
      smRounded: {
        borderRadius: '$radii$md',
        p: '$space$2 $space$4',
        maxHeight: 22,
        ...tooltipStyles,
      },
    },
    color: {
      primary: {
        'backgroundColor': '$primary50',
        'color': '$primaryBase',
        'borderColor': '$primary200',
        '&:hover': {
          borderColor: '$primary400',
        },
      },
      secondary: {
        backgroundColor: 'transparent',
        color: '$gray600',
        borderColor: '$gray700',
      },
      gray: {
        backgroundColor: '$tagGrayBg',
        color: '$text',
        borderColor: '$titleGray',
      },
      warning: {
        backgroundColor: '$warning50',
        color: '$warning700',
        borderColor: '$warning200',
      },
      error: {
        backgroundColor: '$error50',
        color: '$error700',
        borderColor: '$error200',
      },
      errorDark: {
        backgroundColor: '$error100',
        color: '$error700',
        borderColor: '$error300',
      },
      success: {
        backgroundColor: '$success50',
        color: '$success700',
        borderColor: '$success200',
      },
      transparent: {
        backgroundColor: '$inputBg',
        color: '$inputLabel',
        borderColor: '$inputBorder',
      },
      black: {},
      white: {
        backgroundColor: '$whiteBase',
        color: '$gray700',
        borderColor: '$gray300',
      },
      disabled: {},
      purple: {
        backgroundColor: '#E7E4FF',
        color: '#46407b',
        borderColor: '#46407b',
      },
    },
  },
  'defaultVariants': {
    size: 'sm',
    color: 'transparent',
  },
})

const TagCircleVariants = {
  color: {
    primary: {
      background: '$primaryBase',
    },
    secondary: {
      background: '$secondaryBase',
    },
    warning: {
      background: '$warning500',
    },
    error: {
      background: '$error500',
    },
    errorDark: {
      background: '$error700',
    },
    success: {
      background: '$success500',
    },
    gray: {
      background: '$text',
    },
    white: {
      background: '$whiteBase',
    },
    black: {
      background: '$blackBase',
    },
    transparent: {
      background: 'none',
    },
    disabled: {
      background: '$primary$50',
    },
    purple: {
      background: '#46407b',
    },
  },
}

export const TagCircle = styled('span', {
  width: 8,
  height: 8,
  borderRadius: '$radii$rounded',
  variants: {
    color: TagCircleVariants.color,
  },
  defaultVariants: {
    color: 'primary',
  },
})

export const Tag = forwardRef<HTMLSpanElement, TagProps>(
  ({ startIcon, endIcon, children, onClick, onTagClick, ...rest }, ref) => {
    const startIconStyle = css({
      marginRight: startIcon ? '$space$8' : undefined,
    })
    const endIconStyle = css({
      marginLeft: endIcon ? '$space$8' : undefined,
    })

    return (
      <TagCss ref={ref} {...rest} onClick={onTagClick}>
        {startIcon &&
          cloneElement(startIcon as ReactElement, {
            className: startIconStyle.toString(),
            onClick: onClick,
          })}
        {children}
        {endIcon &&
          cloneElement(endIcon as ReactElement, {
            className: endIconStyle.toString(),
            onClick: onClick,
          })}
      </TagCss>
    )
  }
)
